import React, { useContext } from 'react';
import Drawer from '@mui/material/Drawer';
import Icon, { IconType } from '../components/icon';
import * as css from './menu.module.scss';
import { SelectedRoleContext } from '../providers';
import classNames from 'classnames';
import Version from '../components/version/version';
import { RoletypeDto } from '../api/generated/login-generated-api';
import { PsaUserContext } from '../user/PsaUserStore';
import { StudentUserContext } from '../user/StudentUserStore';
import { UhaUserContext } from '../user/UhaUserStore';
import { Link } from 'react-router-dom';
import {
  MenuItemsAdmin,
  MenuItemsPSA,
  MenuItemsStudent,
  MenuItemsUHA,
} from './menuitems';

interface CommonProps {
  children?: React.ReactNode;
}

interface DrawerProps extends CommonProps {
  menuOpen: boolean;
  onClose: () => void;
  permanent?: never;
}

interface PermanentProps extends CommonProps {
  permanent: true;
  menuOpen?: never;
  onClose?: never;
}

type Props = PermanentProps | DrawerProps;

interface User {
  fornavn: string;
  etternavn: string;
  epost: string;
  telefonnummer: string;
  id?: string;
}

type UserInfo = User | undefined;

type RoleType = RoletypeDto | '';

const getMenuItems = (type: RoleType) => {
  switch (type) {
    case RoletypeDto.Praksissted:
      return <MenuItemsPSA />;
    case RoletypeDto.Student:
      return <MenuItemsStudent />;
    case RoletypeDto.Studiested:
      return <MenuItemsUHA />;
    case RoletypeDto.Systemadmin:
      return <MenuItemsAdmin />;
  }
};

const getRoleTypeStyle = (type: RoleType) => {
  switch (type) {
    case RoletypeDto.Praksissted:
      return css.praksissted;
    case RoletypeDto.Student:
      return css.student;
    case RoletypeDto.Studiested:
      return css.studiested;
    case RoletypeDto.Systemadmin:
      return css.sysadmin;
  }

  return '';
};

const getProfilePath = (user: UserInfo, type: RoleType) => {
  switch (type) {
    case RoletypeDto.Praksissted:
      return `/myorganization/praksisstedbrukerprofil/${user?.id}`;
    case RoletypeDto.Studiested:
      return `/myinstitution/studiestedbrukerprofil/${user?.id}`;
  }

  return '';
};

const getFullName = (user: UserInfo) => `${user?.fornavn} ${user?.etternavn}`;

const getNameAbbrev = (user: UserInfo) => {
  const nameArray = user?.fornavn
    .split(/[\s-]+/)
    .concat(user?.etternavn.split(/[\s-]+/));
  let firstNameAbbrev = '';
  let lastNamesAbbrev = '';

  for (let i = 0; i < 3; i++) {
    if (!nameArray || nameArray.length === 0) break;

    if (i === 0) {
      firstNameAbbrev = nameArray.shift()!.charAt(0);
    } else {
      lastNamesAbbrev = `${nameArray.pop()!.charAt(0)}${lastNamesAbbrev}`;
    }
  }

  return firstNameAbbrev + lastNamesAbbrev;
};

const Menu = ({ menuOpen, children, onClose, permanent }: Props) =>
  permanent ? (
    <div className={classNames(css.menu, css.permanent)}>
      <InnerMenu>{children}</InnerMenu>
    </div>
  ) : (
    <Drawer
      open={menuOpen}
      onClose={onClose}
      variant="temporary"
      className={css.menu}
    >
      <InnerMenu>{children}</InnerMenu>
    </Drawer>
  );

const InnerMenu = ({ children }: CommonProps) => {
  const { name, type } = useContext(SelectedRoleContext) || {
    name: '',
    type: '',
  };

  const { uhaUser } = useContext(UhaUserContext);
  const { psaUser } = useContext(PsaUserContext);
  const { studentUser } = useContext(StudentUserContext);
  const user: UserInfo = uhaUser || psaUser || studentUser; // TODO Add SystemadminUserContext, when implemented

  return (
    <>
      <div className={css.contentWrapper}>
        <div className={classNames(css.menuTopItem, getRoleTypeStyle(type))}>
          <span className={css.icon}>
            <Icon className={css.primaryIcon} type={IconType.AccountBalance} />
          </span>
          <span className={css.name}>{name}</span>
        </div>
        {user && <InnerMenuProfile user={user} type={type} />}
        <nav>{children || getMenuItems(type)}</nav>
      </div>
      <footer className={css.menuFooter}>
        <p>Arbeidslivsportalen</p>
        <Version
          className={css.version}
          envClassName={css.env}
          versionClassName={css.version}
        />
      </footer>
    </>
  );
};

interface InnerMenuProfileProps {
  user: UserInfo;
  type: RoleType;
}

const InnerMenuProfile = ({ user, type }: InnerMenuProfileProps) => (
  <InnerMenuProfileWrapper profilePath={getProfilePath(user, type)}>
    <div
      className={classNames(css.menuTopItem, css.userItem)}
      data-cy="userAuthenticatedLanding"
    >
      <div className={css.icon}>
        <p className={css.whiteText}>{getNameAbbrev(user)}</p>
      </div>
      <p className={css.name}>{getFullName(user)}</p>
    </div>
  </InnerMenuProfileWrapper>
);

interface InnerMenuProfileWrapperProps {
  profilePath: string | undefined;
  children: JSX.Element;
}

const InnerMenuProfileWrapper = ({
  profilePath,
  children,
}: InnerMenuProfileWrapperProps) =>
  profilePath ? (
    <Link className={css.profileLink} to={profilePath}>
      {children}
    </Link>
  ) : (
    children
  );

export default Menu;
